import React, { useState, useEffect, useCallback } from "react"
import FormLabel from "@mui/material/FormLabel"
import OutlinedInput from "@mui/material/OutlinedInput"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import { darken, lighten, styled } from "@mui/system"
import Grid from "@mui/material/Grid2"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Button from "@mui/material/Button"
import DeleteIcon from "@mui/icons-material/Delete"
import IconButton from "@mui/material/IconButton"
import Autocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import Alert from "@mui/material/Alert"
import Skeleton from "@mui/material/Skeleton"
import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"

// ---------------------------------------------------------------------------
// Styled helpers (matching LeasesForm conventions)
// ---------------------------------------------------------------------------

const FormGrid = styled(Grid)(() => ({
    display: "flex",
    flexDirection: "column",
}))

const GroupHeader = styled("div")(({ theme }) => ({
    position: "sticky",
    top: "-8px",
    padding: "4px 10px",
    backgroundColor: lighten(theme.palette.secondary.light, 0.85),
    ...theme.applyStyles("dark", {
        backgroundColor: darken(theme.palette.secondary.main, 0.8),
    }),
}))

const GroupItems = styled("ul")({ padding: 0 })

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROPERTY_KINDS = ["Maison", "Appartement", "Garage", "Terrain"]

const EMPTY_PROPERTY = {
    address_1: "",
    address_2: "",
    city: "",
    zip: "",
    country: "",
    kind: "",
    short_name: "",
}

// An owner row in form state: { option: ownerOption | null, shares: string }
const EMPTY_OWNER = { option: null, shares: "" }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Maps a raw DB property_owner row to a form owner row using the loaded
 * ownerOptions list.  Returns { option, shares }.
 */
function mapDbOwnerToFormOwner(po, ownerOptions) {
    const option =
        ownerOptions.find((o) =>
            po.owner_company_id
                ? o.kind === "company" && o.id === po.owner_company_id
                : o.kind === "person" && o.id === po.owner_person_id
        ) ?? null
    return { option, shares: po.shares ?? "" }
}

/**
 * Translates a Supabase error into a user-readable French message.
 * Falls back to the raw message so no information is ever silently swallowed.
 */
function humaniseError(err, context = "opération") {
    if (!err) return null
    // Foreign-key violation (e.g. a lease references this property)
    if (err.code === "23503") {
        return `Impossible d'effectuer cette ${context} : ce bien est encore référencé par d'autres données (bail, etc.).`
    }
    // Unique constraint
    if (err.code === "23505") {
        return `Un bien avec ces données existe déjà (contrainte d'unicité).`
    }
    return err.message ?? `Erreur inattendue lors de la ${context}.`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Form to create, edit and delete a property.
 *
 * Data responsibilities:
 *  - Loads owner options (companies + persons) once on mount via two parallel
 *    Supabase queries.
 *  - Loads the selected property and its owners whenever selectedPropertyId
 *    changes (also two parallel queries).
 *  - Performs all INSERT / UPDATE / DELETE operations directly against Supabase.
 *
 * Race-condition handling for owner mapping:
 *  rawDbOwners (the DB rows) and ownerOptions (the option list) are fetched in
 *  parallel.  A dedicated useEffect watches *both* and re-derives the `owners`
 *  form state whenever either finishes loading — so the form is always correct
 *  regardless of which request resolves first.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string|null} selectedPropertyId
 * @param {() => void} onSaved  — called after a successful save or delete so
 *                                the parent can clear selectedPropertyId
 */
export default function PropertiesForm({ supabase, selectedPropertyId, onSaved }) {

    // -------------------------------------------------------------------------
    // Owner autocomplete options — loaded once on mount
    // -------------------------------------------------------------------------

    const [ownerOptions, setOwnerOptions] = useState([])
    const [optionsLoading, setOptionsLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            const [{ data: companies }, { data: persons }] = await Promise.all([
                supabase.from("company").select("id, name").order("name"),
                supabase
                    .from("person")
                    .select("id, title, first_name, last_name")
                    .order("last_name")
                    .order("first_name"),
            ])
            if (cancelled) return
            setOwnerOptions([
                ...(companies ?? []).map((c) => ({
                    id: c.id,
                    kind: "company",
                    label: c.name,
                })),
                ...(persons ?? []).map((p) => ({
                    id: p.id,
                    kind: "person",
                    label: `${p.title ? p.title + " " : ""}${p.last_name} (${p.first_name})`,
                })),
            ])
            setOptionsLoading(false)
        })()
        return () => { cancelled = true }
    }, [supabase])

    // -------------------------------------------------------------------------
    // Form state
    // -------------------------------------------------------------------------

    const [property, setProperty] = useState(EMPTY_PROPERTY)
    // Raw DB rows — used to re-derive owners once options are ready
    const [rawDbOwners, setRawDbOwners] = useState([])
    // Editable owners in form format
    const [owners, setOwners] = useState([{ ...EMPTY_OWNER }])

    const [formLoading, setFormLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)

    // -------------------------------------------------------------------------
    // Load property data when selectedPropertyId changes
    // -------------------------------------------------------------------------

    useEffect(() => {
        setError(null)

        if (!selectedPropertyId) {
            setProperty(EMPTY_PROPERTY)
            setRawDbOwners([])
            setOwners([{ ...EMPTY_OWNER }])
            return
        }

        let cancelled = false
        setFormLoading(true)
        ;(async () => {
            const [
                { data: prop, error: propErr },
                { data: dbOwners, error: ownersErr },
            ] = await Promise.all([
                supabase.from("property").select("*").eq("id", selectedPropertyId).single(),
                supabase.from("property_owner").select("*").eq("property_id", selectedPropertyId),
            ])

            if (cancelled) return

            if (propErr || ownersErr) {
                setError("Impossible de charger les données du bien.")
                setFormLoading(false)
                return
            }

            setProperty(prop)
            setRawDbOwners(dbOwners ?? [])
            setFormLoading(false)
        })()

        return () => { cancelled = true }
    }, [selectedPropertyId, supabase])

    // -------------------------------------------------------------------------
    // Re-derive form owners whenever raw DB owners OR options change.
    // This cleanly handles the race where options finish loading after the
    // property data (both requests are fired in parallel on mount + selection).
    // -------------------------------------------------------------------------

    useEffect(() => {
        if (!rawDbOwners.length) return
        setOwners(rawDbOwners.map((po) => mapDbOwnerToFormOwner(po, ownerOptions)))
    }, [rawDbOwners, ownerOptions])

    // -------------------------------------------------------------------------
    // Form field handlers
    // -------------------------------------------------------------------------

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target
        setProperty((prev) => ({ ...prev, [name]: value }))
    }, [])

    const handleOwnerOptionChange = useCallback((index, newOption) => {
        setOwners((prev) => {
            const updated = [...prev]
            updated[index] = { ...updated[index], option: newOption }
            return updated
        })
    }, [])

    const handleOwnerSharesChange = useCallback((index, value) => {
        setOwners((prev) => {
            const updated = [...prev]
            updated[index] = { ...updated[index], shares: value }
            return updated
        })
    }, [])

    const addOwner = useCallback(() => {
        setOwners((prev) => [...prev, { ...EMPTY_OWNER }])
    }, [])

    const removeOwner = useCallback((index) => {
        setOwners((prev) => prev.filter((_, i) => i !== index))
    }, [])

    // -------------------------------------------------------------------------
    // Helpers to convert form owners → DB rows
    // -------------------------------------------------------------------------

    const buildOwnerRows = (propertyId) =>
        owners
            .filter((o) => o.option !== null)
            .map((o) => ({
                property_id: propertyId,
                owner_company_id: o.option.kind === "company" ? o.option.id : null,
                owner_person_id: o.option.kind === "person" ? o.option.id : null,
                shares: o.shares !== "" ? parseInt(o.shares, 10) : null,
            }))

    // -------------------------------------------------------------------------
    // Submit (create or update)
    // -------------------------------------------------------------------------

    const handleSubmit = useCallback(async () => {
        setError(null)
        setSubmitting(true)

        // Strip client-only / read-only columns
        const { id: _id, created_at: _ca, updated_at: _ua, ...propertyPayload } = property

        if (selectedPropertyId) {
            // ---- UPDATE ----
            const { error: updateErr } = await supabase
                .from("property")
                .update(propertyPayload)
                .eq("id", selectedPropertyId)

            if (updateErr) {
                setError(humaniseError(updateErr, "mise à jour"))
                setSubmitting(false)
                return
            }

            // Replace owners: delete existing then re-insert.
            // A transaction would be ideal but Supabase JS v2 doesn't expose
            // one directly; delete-then-insert is acceptable for low-concurrency
            // property management data.
            const { error: deleteErr } = await supabase
                .from("property_owner")
                .delete()
                .eq("property_id", selectedPropertyId)

            if (deleteErr) {
                setError(humaniseError(deleteErr, "mise à jour des propriétaires"))
                setSubmitting(false)
                return
            }

            const ownerRows = buildOwnerRows(selectedPropertyId)
            if (ownerRows.length) {
                const { error: insertErr } = await supabase.from("property_owner").insert(ownerRows)
                if (insertErr) {
                    setError(humaniseError(insertErr, "insertion des propriétaires"))
                    setSubmitting(false)
                    return
                }
            }
        } else {
            // ---- INSERT ----
            const { data: inserted, error: insertErr } = await supabase
                .from("property")
                .insert(propertyPayload)
                .select()
                .single()

            if (insertErr || !inserted) {
                setError(humaniseError(insertErr, "création"))
                setSubmitting(false)
                return
            }

            const ownerRows = buildOwnerRows(inserted.id)
            if (ownerRows.length) {
                const { error: ownersErr } = await supabase
                    .from("property_owner")
                    .insert(ownerRows)
                if (ownersErr) {
                    setError(humaniseError(ownersErr, "insertion des propriétaires"))
                    setSubmitting(false)
                    return
                }
            }
        }

        setSubmitting(false)
        setProperty(EMPTY_PROPERTY)
        setRawDbOwners([])
        setOwners([{ ...EMPTY_OWNER }])
        onSaved?.()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [property, owners, selectedPropertyId, supabase, onSaved])

    // -------------------------------------------------------------------------
    // Delete
    // -------------------------------------------------------------------------

    const handleDelete = useCallback(async () => {
        if (!selectedPropertyId) return
        setError(null)
        setSubmitting(true)

        // Delete owners first to avoid FK violations (if no CASCADE is set).
        const { error: deleteOwnersErr } = await supabase
            .from("property_owner")
            .delete()
            .eq("property_id", selectedPropertyId)

        if (deleteOwnersErr) {
            setError(humaniseError(deleteOwnersErr, "suppression des propriétaires"))
            setSubmitting(false)
            return
        }

        const { error: deleteErr } = await supabase
            .from("property")
            .delete()
            .eq("id", selectedPropertyId)

        if (deleteErr) {
            setError(humaniseError(deleteErr, "suppression"))
            setSubmitting(false)
            return
        }

        setSubmitting(false)
        setProperty(EMPTY_PROPERTY)
        setRawDbOwners([])
        setOwners([{ ...EMPTY_OWNER }])
        onSaved?.()
    }, [selectedPropertyId, supabase, onSaved])

    // -------------------------------------------------------------------------
    // Skeleton while loading a selected property
    // -------------------------------------------------------------------------

    if (formLoading) {
        return (
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {Array.from({ length: 7 }).map((_, i) => (
                            <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                        ))}
                    </Box>
                </CardContent>
            </Card>
        )
    }

    // -------------------------------------------------------------------------
    // Form
    // -------------------------------------------------------------------------

    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>

                    {/* Error banner */}
                    {error && (
                        <Grid size={{ xs: 12 }}>
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        </Grid>
                    )}

                    {/* Type de bien */}
                    <FormGrid size={{ xs: 12, md: 6 }}>
                        <FormLabel htmlFor="kind" required>
                            Type de bien
                        </FormLabel>
                        <Select
                            id="kind"
                            name="kind"
                            value={property.kind}
                            onChange={handleInputChange}
                            required
                            size="small"
                        >
                            {PROPERTY_KINDS.map((k) => (
                                <MenuItem key={k} value={k}>
                                    {k}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormGrid>

                    {/* Nom court */}
                    <FormGrid size={{ xs: 12, md: 6 }}>
                        <FormLabel htmlFor="shortName" required>
                            Nom court
                        </FormLabel>
                        <OutlinedInput
                            id="shortName"
                            name="short_name"
                            placeholder="HONO-55"
                            value={property.short_name}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* Adresse */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="address1" required>
                            Adresse
                        </FormLabel>
                        <OutlinedInput
                            id="address1"
                            name="address_1"
                            placeholder="55 Rue du Faubourg Saint-Honoré"
                            value={property.address_1}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* Complément */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="address2">
                            Complément d'adresse
                        </FormLabel>
                        <OutlinedInput
                            id="address2"
                            name="address_2"
                            placeholder="Bureau #1"
                            value={property.address_2}
                            onChange={handleInputChange}
                            size="small"
                        />
                    </FormGrid>

                    {/* Code postal */}
                    <FormGrid size={{ xs: 12, sm: 5 }}>
                        <FormLabel htmlFor="zip" required>
                            Code postal
                        </FormLabel>
                        <OutlinedInput
                            id="zip"
                            name="zip"
                            placeholder="75008"
                            value={property.zip}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* Ville */}
                    <FormGrid size={{ xs: 12, sm: 7 }}>
                        <FormLabel htmlFor="city" required>
                            Ville
                        </FormLabel>
                        <OutlinedInput
                            id="city"
                            name="city"
                            placeholder="Paris"
                            value={property.city}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* Pays */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="country" required>
                            Pays
                        </FormLabel>
                        <OutlinedInput
                            id="country"
                            name="country"
                            placeholder="France"
                            value={property.country}
                            onChange={handleInputChange}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* Propriétaires — Autocomplete following LeasesForm "locataires" pattern */}
                    <FormGrid container spacing={1} size={{ xs: 12 }}>
                        <FormLabel required>Propriétaires</FormLabel>

                        {owners.map((owner, index) => (
                            <Grid container key={index} spacing={1} alignItems="center">

                                {/* Person or company picker */}
                                <FormGrid size={{ xs: 7 }}>
                                    <Autocomplete
                                        disablePortal
                                        disableClearable
                                        loading={optionsLoading}
                                        options={ownerOptions}
                                        groupBy={(option) => option.kind}
                                        getOptionLabel={(option) => option?.label ?? ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option.id === value?.id && option.kind === value?.kind
                                        }
                                        value={owner.option}
                                        onChange={(_, newValue) =>
                                            handleOwnerOptionChange(index, newValue)
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Sélectionner…"
                                                size="small"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {optionsLoading && (
                                                                <CircularProgress color="inherit" size={14} />
                                                            )}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderGroup={(params) => (
                                            <li key={params.key}>
                                                <GroupHeader>
                                                    {params.group === "company"
                                                        ? "Entreprises"
                                                        : "Personnes"}
                                                </GroupHeader>
                                                <GroupItems>{params.children}</GroupItems>
                                            </li>
                                        )}
                                    />
                                </FormGrid>

                                {/* Shares */}
                                <FormGrid size={{ xs: 3 }}>
                                    <OutlinedInput
                                        placeholder="Parts"
                                        value={owner.shares}
                                        onChange={(e) =>
                                            handleOwnerSharesChange(index, e.target.value)
                                        }
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0 }}
                                    />
                                </FormGrid>

                                {/* Delete row */}
                                <FormGrid size={{ xs: 2 }}>
                                    <IconButton
                                        color="error"
                                        onClick={() => removeOwner(index)}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </FormGrid>
                            </Grid>
                        ))}

                        <Button variant="text" onClick={addOwner} size="small">
                            Ajouter un propriétaire
                        </Button>
                    </FormGrid>

                    {/* Action buttons */}
                    <FormGrid
                        size={{ xs: selectedPropertyId ? 6 : 12 }}
                        sx={{ mt: 1 }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={
                                submitting && !selectedPropertyId
                                    ? <CircularProgress size={14} color="inherit" />
                                    : null
                            }
                        >
                            Valider
                        </Button>
                    </FormGrid>

                    {selectedPropertyId && (
                        <FormGrid size={{ xs: 6 }} sx={{ mt: 1 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={submitting}
                                startIcon={
                                    submitting
                                        ? <CircularProgress size={14} color="inherit" />
                                        : null
                                }
                            >
                                Supprimer
                            </Button>
                        </FormGrid>
                    )}

                </Grid>
            </CardContent>
        </Card>
    )
}